import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Col, ContentPlaceholder, Row } from '@evertel/web/ui'

interface RoomLoadingStateProps {
    variant?: 'full' | 'wall'
    // Indicator (e.g. spinner + "Decrypting messages…") overlaid on the `wall` skeleton.
    children?: ReactNode
}

// Approx height of one message row (avatar / 3 lines + py-4 + mb-4). Used to
// compute how many rows fit the available space so the list fills it snugly.
const ROW_HEIGHT = 160
const MIN_ROWS = 1

const BODY_WIDTH_SETS = [
    ['40%', '60%', '80%'],
    ['48%', '66%', '88%'],
    ['44%', '62%', '78%'],
    ['38%', '58%', '92%']
]

const MessageRowSkeleton: React.FC<{ rowIndex: number }> = ({ rowIndex }) => {
    const widths = BODY_WIDTH_SETS[rowIndex % BODY_WIDTH_SETS.length]

    return (
        <Row valign="start" className="mb-4 py-4">
            <ContentPlaceholder width={40} height={40} className="flex-shrink-0" />
            <Col className="ms-3">
                <ContentPlaceholder width={140} height={18} />
                {widths.map((w, i) => (
                    <ContentPlaceholder key={i} width={w} height={18} className="mt-2" />
                ))}
            </Col>
        </Row>
    )
}

const MessageListSkeleton: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null)
    const [rowCount, setRowCount] = useState(MIN_ROWS)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        const observer = new ResizeObserver(([entry]) => {
            const height = entry.contentRect.height
            setRowCount(Math.max(MIN_ROWS, Math.floor(height / ROW_HEIGHT)))
        })
        observer.observe(el)

        return () => observer.disconnect()
    }, [])

    return (
        <div ref={ref} className="flex-grow-1 overflow-hidden h-100">
            {Array.from({ length: rowCount }).map((_, i) => (
                <MessageRowSkeleton key={i} rowIndex={i} />
            ))}
        </div>
    )
}

const MessageHeaderSkeleton: React.FC = () => (
    <Row valign="center" className="pb-3 border-bottom flex-shrink-0">
        <ContentPlaceholder width="40%" height={30} style={{ maxWidth: 220 }} />
        <div className="d-flex gap-2 ms-auto flex-shrink-1 align-items-center me-2">
            <ContentPlaceholder shape="circle" width={28} height={28} />
            <ContentPlaceholder width={90} height={15} />
        </div>
    </Row>
)

const CreateMessageSkeleton: React.FC = () => (
    <div className="pe-3 flex-shrink-0">
        <ContentPlaceholder height={80} />
    </div>
)

const RightGutterSkeleton: React.FC = () => (
    <div
        className="d-flex flex-column align-items-center border-start flex-shrink-0 ps-2 p-1"
    >
        {Array.from({ length: 5 }).map((_, i) => (
            <ContentPlaceholder key={i} width={40} height={40} className="mb-3" />
        ))}
    </div>
)

const RoomLoadingState: React.FC<RoomLoadingStateProps> = ({ variant = 'full', children }) => {
    // `wall` covers the message area (header/create/gutter already mounted): the
    // message skeleton fills the space and `children` (spinner + status text) are
    // overlaid centered, so the "loading messages" context reads on top of it.
    if (variant === 'wall') {
        return (
            <div
                className="position-relative h-100 overflow-hidden p-2"
                style={{ backgroundColor: 'var(--bs-tertiary-bg)' }}
            >
                <MessageListSkeleton />
                {children && (
                    <div
                        className="position-absolute top-0 start-50 translate-middle-x mt-3 d-flex align-items-center gap-2 px-1 py-0 me-1 rounded-pill shadow-sm border"
                        style={{ backgroundColor: 'var(--bs-body-bg)' }}
                    >
                        {children}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div
            className="d-flex p-3"
            style={{
                height: 'calc(100vh - var(--header-height))',
                backgroundColor: 'var(--bs-tertiary-bg)'
            }}
        >
            <div className="d-flex flex-column flex-grow-1 overflow-hidden">
                <MessageHeaderSkeleton />
                <MessageListSkeleton />
                <CreateMessageSkeleton />
            </div>
            <RightGutterSkeleton />
        </div>
    )
}

RoomLoadingState.displayName = 'RoomLoadingState'
export { RoomLoadingState }
